import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {ReturnType<typeof setTimeout> | null} */
  #rebuyRefreshTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    
    // Listen for Rebuy cart events to refresh cart drawer when Rebuy adds products
    document.addEventListener('rebuy:cart.add', this.#handleRebuyCartChange);
    document.addEventListener('rebuy:cart.change', this.#handleRebuyCartChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('rebuy:cart.add', this.#handleRebuyCartChange);
    document.removeEventListener('rebuy:cart.change', this.#handleRebuyCartChange);
    
    // Clear any pending refresh timeout
    if (this.#rebuyRefreshTimeout) {
      clearTimeout(this.#rebuyRefreshTimeout);
      this.#rebuyRefreshTimeout = null;
    }
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Handles Rebuy cart events to refresh cart drawer content when Rebuy adds products
   * (e.g., free gifts via GWP rules)
   * @param {CustomEvent} event - The Rebuy cart event
   */
  #handleRebuyCartChange = (event) => {
    // Only refresh if the drawer is currently open or was just opened
    if (!this.isOpen) return;

    // Clear any existing timeout to debounce rapid Rebuy events
    if (this.#rebuyRefreshTimeout) {
      clearTimeout(this.#rebuyRefreshTimeout);
    }

    // Add a small delay to ensure Rebuy has finished updating the cart
    // This handles cases where multiple Rebuy events fire in quick succession
    this.#rebuyRefreshTimeout = setTimeout(() => {
      this.#refreshCartSections();
      this.#rebuyRefreshTimeout = null;
    }, 100);
  };

  /**
   * Refreshes all cart sections to show updated cart content
   */
  #refreshCartSections() {
    // Find all cart-items-component elements and refresh their sections
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    
    cartItemsComponents.forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        const sectionId = component.dataset.sectionId;
        // Refresh the section to show newly added items
        sectionRenderer.renderSection(sectionId, { cache: false });
      }
    });
  }

  /**
   * Checks if the cart drawer is currently open
   * @returns {boolean}
   */
  get isOpen() {
    return this.refs.dialog?.open === true;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
